"use client";

import { useState, useRef, useEffect, useCallback, Fragment } from "react";
import { createPortal } from "react-dom";
import { compareFolders, type SessionListItem, type FolderResponse } from "@/app/lib/types";

// ---------------------------------------------------------------------------
// Relative time formatter
// ---------------------------------------------------------------------------

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SessionItemProps {
  session: SessionListItem;
  isActive: boolean;
  onClick: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onFlag: (id: string) => void;
  onMoveToFolder?: (id: string) => void;
  onShare?: (id: string) => void;
  folderName?: string;
  currentUserEmail?: string;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  // Inline "Move to Folder" submenu in the three-dots menu.
  folders?: FolderResponse[];
  onMoveSessionToFolder?: (sessionId: string, folderId: string) => void; // "" = Unfiled
  onMoveToNewFolder?: (sessionId: string) => void;
}

export default function SessionItem({
  session,
  isActive,
  onClick,
  onDelete,
  onRename,
  onFlag,
  onMoveToFolder,
  onShare,
  currentUserEmail,
  folderName,
  selectionMode,
  selected,
  onToggleSelect,
  folders,
  onMoveSessionToFolder,
  onMoveToNewFolder,
}: SessionItemProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showMoveSubmenu, setShowMoveSubmenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(session.title);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // "Move to Folder" cascade flyout — portaled to <body> so the sidebar's
  // scroll/overflow can't clip it; positioned to the right of the menu item.
  const moveItemRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const submenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const measureCanvas = useRef<HTMLCanvasElement | null>(null);
  const [submenuPos, setSubmenuPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  const openMoveSubmenu = useCallback(() => {
    if (submenuCloseTimer.current) { clearTimeout(submenuCloseTimer.current); submenuCloseTimer.current = null; }
    const r = moveItemRef.current?.getBoundingClientRect();
    if (r) {
      const labels = ["+ Create New", "Unfiled", ...(folders || []).map((f) => f.name)];
      // Vertical: prefer opening level with the item, but never let the box
      // run off the bottom — shift it up so its bottom sits at the screen
      // bottom, and cap its height to the space that's actually available.
      const margin = 8;
      const maxBoxH = 480;
      const viewportH = window.innerHeight;
      const estH = Math.min(labels.length * 34 + 8, maxBoxH);
      const top = Math.max(margin, Math.min(r.top, viewportH - margin - estH));
      const maxHeight = Math.min(maxBoxH, viewportH - margin - top);
      // Width = 1.3x the longest label's rendered text (+ button/box chrome so
      // the longest name never ellipsizes). Beats a fixed, too-wide minWidth.
      const cv = (measureCanvas.current ||= document.createElement("canvas"));
      const ctx = cv.getContext("2d");
      let maxText = 0;
      if (ctx) {
        const btn = moveItemRef.current?.querySelector("button");
        const cs = btn ? getComputedStyle(btn) : null;
        ctx.font = cs ? `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}` : "600 13px system-ui";
        for (const l of labels) maxText = Math.max(maxText, ctx.measureText(l).width);
      }
      const width = Math.min(Math.round(maxText * 1.3) + 28, Math.round(window.innerWidth * 0.6));
      setSubmenuPos({ top, left: r.right + 4, width, maxHeight });
    }
    setShowMoveSubmenu(true);
  }, [folders]);
  const scheduleCloseMoveSubmenu = useCallback(() => {
    if (submenuCloseTimer.current) clearTimeout(submenuCloseTimer.current);
    submenuCloseTimer.current = setTimeout(() => setShowMoveSubmenu(false), 120);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (submenuRef.current?.contains(t)) return; // clicks in the portaled flyout
      setShowMenu(false);
      setShowMoveSubmenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  // Focus input when renaming
  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  const handleContextMenu = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setShowMenu(true);
  }, []);

  // Long-press for mobile context menu
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const handleTouchStart = useCallback(() => {
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setShowMenu(true);
    }, 500);
  }, []);
  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    // If it was a short tap (not long-press), trigger click immediately
    if (!longPressFired.current) {
      onClick();
    }
  }, [onClick]);

  const handleRenameSubmit = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== session.title) {
      onRename(session.id, trimmed);
    }
    setIsRenaming(false);
    setShowMenu(false);
  }, [renameValue, session.id, session.title, onRename]);

  if (isRenaming) {
    return (
      <div className={`session-item ${isActive ? "session-item-active" : ""}`}>
        <input
          ref={inputRef}
          type="text"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleRenameSubmit();
            if (e.key === "Escape") {
              setRenameValue(session.title);
              setIsRenaming(false);
            }
          }}
          style={{
            width: "100%",
            padding: "4px 8px",
            fontSize: "13px",
            background: "var(--surface-hover)",
            border: "1px solid var(--accent)",
            borderRadius: "4px",
            color: "var(--text-primary)",
            outline: "none",
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={`session-item ${isActive ? "session-item-active" : ""}`}
      onClick={selectionMode ? () => onToggleSelect?.(session.id) : onClick}
      onContextMenu={selectionMode ? undefined : handleContextMenu}
      onTouchStart={selectionMode ? undefined : handleTouchStart}
      onTouchEnd={selectionMode ? undefined : handleTouchEnd}
      onTouchMove={selectionMode ? undefined : handleTouchEnd}
      style={{ position: "relative", alignItems: "center", userSelect: "none", WebkitUserSelect: "none" }}
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            border: selected ? "none" : "1.5px solid var(--text-tertiary)",
            background: selected ? "var(--accent)" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            marginRight: "8px",
            transition: "all var(--transition-fast)",
          }}
        >
          {selected && (
            <svg width="8" height="8" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8l4 4 6-7" />
            </svg>
          )}
        </div>
      )}
      <div
        style={{ flex: 1, minWidth: 0 }}
        title={(() => {
          const parts: string[] = [formatRelativeTime(session.updated_at)];
          if (session.shared && session.owner_email === currentUserEmail && session.participants && session.participants.length > 0) {
            parts.push(`Shared with ${session.participants.join(", ")}`);
          } else if (session.shared_by && session.shared_by !== currentUserEmail) {
            parts.push(`Shared by ${session.shared_by}`);
          }
          if (folderName) parts.push(`Folder: ${folderName}`);
          return `${session.title || "Untitled"}\n${parts.join(" · ")}`;
        })()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          {session.flagged && (
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "var(--warning)",
                flexShrink: 0,
              }}
            />
          )}
          <span
            style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--text-primary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
              minWidth: 0,
            }}
          >
            {session.title || "Untitled"}
          </span>
          {/* Scheduled-origin badge — this session was created by a headless
              scheduled custom-query run. Small clock glyph. */}
          {session.from_schedule && (
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="var(--text-tertiary)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
              aria-label="Scheduled"
            >
              <circle cx="8" cy="8" r="6.5" />
              <path d="M8 4.5V8l2.5 1.5" />
            </svg>
          )}
          {(session.attachment_count ?? 0) > 0 && (
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="var(--text-tertiary)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
              aria-label={`${session.attachment_count} ${session.attachment_count === 1 ? "file" : "files"}`}
            >
              <path d="M13.25 7.25 7.5 13a3.5 3.5 0 0 1-4.95-4.95l6-6a2.25 2.25 0 1 1 3.18 3.18l-6 6a1 1 0 0 1-1.41-1.41l5.3-5.3" />
            </svg>
          )}
          {/* Shared icon */}
          {(session.shared || session.shared_by) && (
            <svg width="12" height="12" viewBox="0 0 16 16" fill="var(--text-tertiary)" style={{ flexShrink: 0 }}>
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
          )}
          {folderName && (
            <span
              style={{
                fontSize: "10px",
                padding: "0 5px",
                borderRadius: "8px",
                background: "var(--border)",
                color: "var(--text-secondary)",
                flexShrink: 0,
                lineHeight: "16px",
                maxWidth: "80px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
              }}
            >
              {folderName}
            </span>
          )}
        </div>
      </div>

      {/* Three-dot menu button — absolutely positioned to not eat layout space */}
      {!selectionMode && <button
        className="btn-ghost session-item-menu"
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
          setShowMoveSubmenu(false);
        }}
        style={{
          padding: 0,
          opacity: 0,
          transition: "opacity var(--transition-fast)",
          width: "16px",
          height: "16px",
          position: "absolute",
          right: "4px",
          top: "50%",
          transform: "translateY(-50%)",
          background: "var(--surface-hover)",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        aria-label="Session options"
      >
        <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>}

      {/* Context menu */}
      {!selectionMode && showMenu && (
        <div
          ref={menuRef}
          style={{
            position: "absolute",
            right: "8px",
            top: "100%",
            zIndex: 20,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            padding: "4px",
            minWidth: "140px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}
        >
          <button
            className="btn-ghost"
            onClick={(e) => {
              e.stopPropagation();
              setIsRenaming(true);
              setShowMenu(false);
            }}
            style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px" }}
          >
            Rename
          </button>
          <button
            className="btn-ghost"
            onClick={(e) => {
              e.stopPropagation();
              onFlag(session.id);
              setShowMenu(false);
            }}
            style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px" }}
          >
            {session.flagged ? "Unflag" : "Flag"}
          </button>
          {onMoveSessionToFolder ? (
            <div
              ref={moveItemRef}
              onMouseEnter={openMoveSubmenu}
              onMouseLeave={scheduleCloseMoveSubmenu}
            >
              <button
                className="btn-ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  if (showMoveSubmenu) setShowMoveSubmenu(false);
                  else openMoveSubmenu();
                }}
                style={{ width: "100%", justifyContent: "space-between", padding: "6px 10px", display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span>Move to Folder</span>
                <span style={{ opacity: 0.6 }}>›</span>
              </button>
              {showMenu && showMoveSubmenu && submenuPos && createPortal(
                <div
                  ref={submenuRef}
                  onMouseEnter={openMoveSubmenu}
                  onMouseLeave={scheduleCloseMoveSubmenu}
                  style={{
                    position: "fixed", top: submenuPos.top, left: submenuPos.left,
                    width: submenuPos.width,
                    background: "var(--surface)", border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)", padding: "4px",
                    maxHeight: submenuPos.maxHeight, overflowY: "auto", zIndex: 1000,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                  }}
                >
                  <button
                    className="btn-ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveToNewFolder?.(session.id);
                      setShowMenu(false);
                      setShowMoveSubmenu(false);
                    }}
                    style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px", fontWeight: 600, color: "var(--accent, var(--text))" }}
                  >
                    + Create New
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveSessionToFolder(session.id, "");
                      setShowMenu(false);
                      setShowMoveSubmenu(false);
                    }}
                    style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px" }}
                  >
                    Unfiled
                  </button>
                  {[...(folders || [])].sort(compareFolders).map((f) => (
                    <Fragment key={f.id}>
                      {f.separator_before && <div style={{ height: "2px", background: "#484848", borderRadius: "1px", margin: "4px 6px" }} />}
                      <button
                        className="btn-ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveSessionToFolder(session.id, f.id);
                          setShowMenu(false);
                          setShowMoveSubmenu(false);
                        }}
                        style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {f.name}
                      </button>
                    </Fragment>
                  ))}
                </div>,
                document.body
              )}
            </div>
          ) : onMoveToFolder && (
            <button
              className="btn-ghost"
              onClick={(e) => {
                e.stopPropagation();
                onMoveToFolder(session.id);
                setShowMenu(false);
              }}
              style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px" }}
            >
              Move to Folder
            </button>
          )}
          {onShare && (
            <button
              className="btn-ghost"
              onClick={(e) => {
                e.stopPropagation();
                onShare(session.id);
                setShowMenu(false);
              }}
              style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px" }}
            >
              Share
            </button>
          )}
          <button
            className="btn-ghost"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(session.id);
              setShowMenu(false);
            }}
            style={{
              width: "100%",
              justifyContent: "flex-start",
              padding: "6px 10px",
              color: "var(--error)",
            }}
          >
            {/* Shared-to-me sessions: show "Leave" since the user can't
                actually delete someone else's chat, only leave the share.
                page.tsx's handler already calls leaveSharedSession for
                this case — the label just makes the intent visible. */}
            {session.shared && session.owner_email && session.owner_email !== currentUserEmail
              ? "Leave"
              : "Delete"}
          </button>
        </div>
      )}
    </div>
  );
}
